// @safetyhub/report-composer — 조립형 dataDef assembler 레지스트리 (N5-2b)
//
// compute가 필요한 데이터를 "출력 명명 배열"로 노출하되, 입력은 작성폼이 바인딩한다(방향 반전).
// 표는 일반 repeat처럼 over로 출력 배열을 소비(compute를 모름). 서로 다른 카디널리티 2배열을
// 병합(max-length zip)하는 알고리즘적 작업만 등록형 assembler로 처리한다.
//
// ⚠️ 완전 변환 DSL이 아니다 — 제한된 zip + 선언적 field-map(dotted-path)만 지원한다.
// 도메인(SMS 등) 하드코딩 절대 금지: assembler는 제네릭, 칼럼 매핑은 options로 선언한다.

import type { DataDefCompute } from '@safetyhub/shared';
import { getByPath } from './resolver';

/**
 * assembler 함수: 입력값 맵(이미 resolve된 값) + options → 출력 행 배열.
 *
 * context(선택)는 실행 컨텍스트 — `inputPaths`는 compute.inputs(입력명 → content dotted-path)
 * 원문이다. joinByKey가 조인 소스 칼럼의 접두 자동 산출(`${inputPaths.join}.${칼럼}`)에 쓴다.
 * 미전달(구버전 호출)이면 접두 산출만 생략되고 나머지 동작은 동일하다.
 */
export type Assembler = (
  inputs: Record<string, unknown>,
  options: Record<string, unknown> | undefined,
  context?: { inputPaths?: Record<string, string> },
) => Record<string, unknown>[];

/** 값이 배열이면 그대로, 아니면 빈 배열(안전 가드). */
function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * 제네릭 2소스 zip assembler — 서로 다른 길이의 left/right 배열을 max-length로 zip한다.
 *
 * 입력:
 *   - inputs.left  : 좌측 배열(아니면 [])
 *   - inputs.right : 우측 배열(아니면 [])
 * options(선언적 field-map):
 *   - leftFields?:  Record<출력키, left 항목 내 dotted-path>
 *   - rightFields?: Record<출력키, right 항목 내 dotted-path>
 *   - emptyValue?:  짧은 쪽 패딩값(미지정 시 '')
 *
 * 동작: n = max(left.length, right.length). i∈[0,n) 행마다 leftFields/rightFields를
 * 적용해 출력키 ← 항목 dotted-path 값(범위 밖이면 emptyValue, 추출값이 null/undefined여도 emptyValue).
 * field-map이 둘 다 없으면 항목 전체 spread fallback({...left[i], ...right[i]}).
 */
const zipTwoSources: Assembler = (inputs, options) => {
  const left = asArray(inputs.left);
  const right = asArray(inputs.right);
  const opts = options ?? {};
  const leftFields = opts.leftFields as Record<string, string> | undefined;
  const rightFields = opts.rightFields as Record<string, string> | undefined;
  const emptyValue = 'emptyValue' in opts ? opts.emptyValue : '';

  const n = Math.max(left.length, right.length);
  const rows: Record<string, unknown>[] = [];

  const hasFieldMap = Boolean(leftFields) || Boolean(rightFields);

  for (let i = 0; i < n; i++) {
    if (!hasFieldMap) {
      // field-map 미지정 → 항목 전체 spread fallback(범위 밖은 빈 객체).
      const l = i < left.length ? (left[i] as Record<string, unknown> | undefined) : undefined;
      const r = i < right.length ? (right[i] as Record<string, unknown> | undefined) : undefined;
      rows.push({ ...(l ?? {}), ...(r ?? {}) });
      continue;
    }

    const row: Record<string, unknown> = {};
    if (leftFields) {
      for (const [outKey, srcPath] of Object.entries(leftFields)) {
        row[outKey] =
          i < left.length ? getByPath(left[i], srcPath) ?? emptyValue : emptyValue;
      }
    }
    if (rightFields) {
      for (const [outKey, srcPath] of Object.entries(rightFields)) {
        row[outKey] =
          i < right.length ? getByPath(right[i], srcPath) ?? emptyValue : emptyValue;
      }
    }
    rows.push(row);
  }

  return rows;
};

/**
 * 제네릭 slice assembler — 한 소스 배열을 `[start, end)` 구간으로 자른다.
 *
 * 입력:
 *   - inputs.source : 소스 배열(아니면 [])
 * options:
 *   - start?: number (기본 0)
 *   - end?:   number (기본 끝까지)
 *
 * "N개 후 다음 영역"(예: 좌측 12개 → 우측) 레이아웃에, 한 소스 dataDef를 두 파생 dataDef
 * (slice `[0,N)` / `[N,)`)로 나눠 좌/우 repeat 영역이 각각 소비하게 한다. 도메인 비종속(범용).
 */
const sliceRange: Assembler = (inputs, options) => {
  const source = asArray(inputs.source);
  const opts = options ?? {};
  const start = typeof opts.start === 'number' ? opts.start : 0;
  const end = typeof opts.end === 'number' ? opts.end : undefined;
  return source.slice(start, end) as Record<string, unknown>[];
};

/**
 * 제네릭 key 조인 assembler — base 배열 각 행에, join 배열에서 조인 키가 일치하는 행의 지정 필드를
 * 병합한다. index가 아니라 **키(예: findingId) equi-match**라 두 소스의 집합·순서가 달라도 정확
 * (위험성평가=HAZARD finding만 / 이행상태=전체 finding 처럼 상위집합 관계에서 필수).
 *
 * 입력:
 *   - inputs.base : 기준 배열(행 수·순서 정본, 아니면 [])
 *   - inputs.join : 조인 소스 배열(아니면 [])
 * options:
 *   - baseKey:     base 항목 내 조인 키 dotted-path (예: 'id')
 *   - joinKey:     join 항목 내 조인 키 dotted-path (예: 'findingId')
 *   - mergeFields: Record<출력키, join 항목 내 dotted-path> — 매칭 시 병합, 무매칭이면 emptyValue
 *   - emptyValue?: 무매칭 병합값(기본 '')
 *
 * base 행 전체를 보존(spread)한 뒤 mergeFields를 덮는다. join은 키로 1회 인덱싱(첫 매칭 우선).
 * 키가 비면(null/undefined) 무매칭 취급. base가 정본이라 join에만 있는 행은 버려진다.
 *
 * 접두 자동 산출(2026-07-26): context.inputPaths.join(= compute.inputs.join 경로)이 있으면,
 * 조인 소스의 **모든 top-level 칼럼**을 `${경로}.${칼럼}` flat 키로 base 각 행에 싣는다
 * (매칭 시 그 행의 값, 무매칭·값 null/undefined면 emptyValue). 점 포함 키는 flat-first 읽기
 * (RenderTemplate readBindingValue / resolver resolveBinding)가 소비하므로 별도 저작 없이
 * 조인 소스 전 칼럼이 바인딩 후보가 된다. mergeFields는 레거시 하위호환으로 계속 소비하며
 * 접두 칼럼 **뒤에** 덮는다(명시 저작 우선).
 */
const joinByKey: Assembler = (inputs, options, context) => {
  const base = asArray(inputs.base);
  const join = asArray(inputs.join);
  const opts = options ?? {};
  const baseKey = opts.baseKey as string | undefined;
  const joinKey = opts.joinKey as string | undefined;
  const mergeFields = opts.mergeFields as Record<string, string> | undefined;
  const emptyValue = 'emptyValue' in opts ? opts.emptyValue : '';
  if (!baseKey || !joinKey) return base.map((b) => ({ ...(b as Record<string, unknown>) }));

  const joinIndex = new Map<unknown, Record<string, unknown>>();
  for (const j of join) {
    const k = getByPath(j, joinKey);
    if (k !== undefined && k !== null && !joinIndex.has(k)) {
      joinIndex.set(k, j as Record<string, unknown>);
    }
  }
  // 접두 자동 산출 대상 칼럼 — join 배열 각 행의 top-level 키 합집합(행마다 형상이 달라도 전량).
  const prefix = context?.inputPaths?.join;
  const joinCols = new Set<string>();
  if (prefix) {
    for (const j of join) {
      if (j && typeof j === 'object' && !Array.isArray(j)) {
        for (const col of Object.keys(j as Record<string, unknown>)) joinCols.add(col);
      }
    }
  }
  return base.map((b) => {
    const row: Record<string, unknown> = { ...(b as Record<string, unknown>) };
    const k = getByPath(b, baseKey);
    const matched = k !== undefined && k !== null ? joinIndex.get(k) : undefined;
    if (prefix) {
      for (const col of joinCols) {
        row[`${prefix}.${col}`] = matched ? matched[col] ?? emptyValue : emptyValue;
      }
    }
    if (mergeFields) {
      for (const [outKey, srcPath] of Object.entries(mergeFields)) {
        row[outKey] = matched ? getByPath(matched, srcPath) ?? emptyValue : emptyValue;
      }
    }
    return row;
  });
};

/** 등록형 assembler 레지스트리(키 → 함수). 신규 assembler는 여기에 등록. */
export const assemblerRegistry: Record<string, Assembler> = {
  'zip-two-sources': zipTwoSources,
  'slice-range': sliceRange,
  'join-by-key': joinByKey,
};

/**
 * compute 실행: registry에서 assembler를 찾아 resolve된 입력 맵으로 행 배열을 산출한다.
 * 미등록 assembler면 null(호출부가 defaultRows로 fallback).
 * compute.inputs 원문을 context.inputPaths로 함께 전달한다(접두 자동 산출의 접두 —
 * 호출부(resolver/캔버스)는 시그니처 변경 없이 자동 반영된다).
 */
export function runAssembler(
  compute: DataDefCompute,
  resolvedInputs: Record<string, unknown>,
): Record<string, unknown>[] | null {
  const fn = assemblerRegistry[compute.assembler];
  if (!fn) return null;
  return fn(resolvedInputs, compute.options, { inputPaths: compute.inputs });
}
